import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { ShieldCheck, UserPlus } from "lucide-react";

export default function PortalSignup() {
  const { register } = useCustomerAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) return setError("Password must be at least 8 characters");
    setLoading(true);
    try {
      await register(form);
      toast.success("Welcome to Tech Alpha SSL.");
      nav("/portal");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Sign up failed");
    } finally { setLoading(false); }
  };

  return (
    <>
      <SEO title="Client Portal — Sign Up | Tech Alpha SSL" description="Create your Tech Alpha SSL client portal account." />
      <section className="min-h-[calc(100vh-4rem)] grid place-items-center py-16">
        <div className="w-full max-w-md container-page">
          <div className="card-elevated p-8">
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-lg bg-gradient-to-br from-[#1565FF] to-[#4FC3F7] text-white">
                <ShieldCheck className="h-5 w-5" />
              </span>
              <div>
                <h1 className="text-lg font-semibold tracking-tight">Create your account</h1>
                <p className="text-xs text-muted-foreground">Access orders, invoices, and support.</p>
              </div>
            </div>
            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="portal-signup-form">
              <Input label="Full name" required value={form.name} onChange={upd("name")} testId="portal-signup-name" />
              <Input label="Email" type="email" required value={form.email} onChange={upd("email")} testId="portal-signup-email" />
              <Input label="Password (min 8 chars)" type="password" required value={form.password} onChange={upd("password")} testId="portal-signup-password" />
              <Input label="Company (optional)" value={form.company} onChange={upd("company")} testId="portal-signup-company" />
              <Input label="Phone (optional)" value={form.phone} onChange={upd("phone")} testId="portal-signup-phone" />
              {error && <p className="text-sm text-destructive" data-testid="portal-signup-error">{error}</p>}
              <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="portal-signup-submit">
                <UserPlus className="h-4 w-4" /> {loading ? "Creating..." : "Create account"}
              </button>
              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <Link to="/portal/login" className="text-[#1565FF] font-medium" data-testid="portal-signup-goto-login">Sign in</Link>
              </p>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

function Input({ label, type = "text", required, value, onChange, testId }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <input type={type} required={required} value={value} onChange={onChange} data-testid={testId}
        className="mt-1 w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm outline-none focus:border-[#1565FF]" />
    </div>
  );
}
